import { UsageType } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/phone_number.pb';
import { AxiosError } from 'axios';
import { formatPhoneFaxNumber, formatPhoneNumber } from 'helpers/format-phone';
import {
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
} from 'react-query';
import { getPhoneNumbers, portingSchemaApi, uploadPhoneFile } from './porting.api';
import {
  CancelPortInOrderRequest,
  ClonePortingDataRequest,
  ClonePortingDataResponse,
  CreateSalesforcePortOrderRequest,
  CreateSalesforcePortOrderResponse,
  DeletePortOrderDocumentRequest,
  DeletePortOrderDocumentResponse,
  ExtendedRelationalPhoneNumber,
  GetAvailableFOCDatesRequest,
  GetAvailableFOCDatesResponse,
  GetPortingAuditRevisionDetailsRequest,
  GetPortingAuditRevisionDetailsResponse,
  GetPortingValidationsByPortingDataIDRequest,
  GetPortingValidationsByPortingDataIDResponse,
  ListPhoneNumbersRequest,
  ListPhoneNumbersResponse,
  NumberType,
  MediaType as OldMediaType,
  GetPortingAuditSummaryRequest,
  GetPortingAuditSummaryResponse,
  PortingDataByLocationIDResponse,
  PortingDataUpdateRequest,
  PortingDataUpdateResponse,
  PortingMediaType,
  ReadPortingDataByIDRequest,
  ReadPortingDataByIDResponse,
  UpdatePortOrderDocumentResponse,
  UploadPortOrderDocumentResponse,
} from './porting.types';
import {
  CheckPhoneNumbersExistRequest,
  CheckPhoneNumbersExistResponse,
  PhoneNumberService,
} from '@weave/schema-gen-ts/dist/schemas/phone/number-service/v2/number_service.pb';
import {
  SMSHostabilityCheckRequest,
  SMSHostabilityCheckResponse,
  PortInSMSCreateByPortingDataIDRequest,
  PortInSMSCreateByPortingDataIDResponse,
  PortingDataService,
} from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import { authorizedFetch } from 'helpers/utils';

const PortingQueryKeys = {
  baseQueryKey: ['porting'],
  checkSmsHostability: (phoneNumber: string) => [
    ...PortingQueryKeys.baseQueryKey,
    'check-sms-hostability',
    phoneNumber,
  ],
  getPortOrderList: (locationId: string) => [
    ...PortingQueryKeys.baseQueryKey,
    'port-order-list',
    locationId,
  ],
  getPortingDetails: (request: ReadPortingDataByIDRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    'porting-details',
    request.portingDataId,
  ],
  getPhoneNumbers: (request: ListPhoneNumbersRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    'get-phone-numbers',
    ...(request.locationIds || []),
  ],
  getAvailableFOCDates: (request: GetAvailableFOCDatesRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    'get-available-foc-dates',
    request.portingDataId,
    request.provider,
  ],
  getPortingValidationData: (request: GetPortingValidationsByPortingDataIDRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    request.portingDataId,
  ],
  getPortingAuditHistorySummary: (request: GetPortingAuditSummaryRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    'audit-history',
    request.portingDataId,
  ],
  getPortingAuditRevisionDetails: (request: GetPortingAuditRevisionDetailsRequest) => [
    ...PortingQueryKeys.baseQueryKey,
    'revision-details',
    request.portingDataId,
    request.revisionId,
  ],
  checkPhoneExists: (phoneNumber: string) => [
    ...PortingQueryKeys.baseQueryKey,
    'check-phone-exists',
    phoneNumber,
  ],
};

const useGetPhoneNumbers = (
  request: ListPhoneNumbersRequest,
  options?: UseQueryOptions<
    ListPhoneNumbersResponse,
    unknown,
    ExtendedRelationalPhoneNumber[]
  >
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPhoneNumbers(request),
    queryFn: async () => (await getPhoneNumbers(request)).data,
    retry: false,
    ...options,
    enabled: !!request.locationIds?.length && (options?.enabled ?? true),
    select: (data) => {
      return (data.phoneNumbers ?? [])?.map((phoneNumberData) => {
        const phoneNumber =
          phoneNumberData?.number?.phoneNumber?.phoneNumber?.nationalNumber?.toString() ??
          '';
        const type = phoneNumberData?.number?.faxProvider?.id
          ? NumberType.FAX
          : NumberType.PHONE;

        const usageType =
          phoneNumberData?.number?.phoneNumber?.usageType ||
          UsageType.USAGE_TYPE_UNSPECIFIED;

        const formattedPhoneNumber =
          type === NumberType.FAX
            ? formatPhoneFaxNumber(phoneNumber)
            : formatPhoneNumber(phoneNumber);
        return {
          ...phoneNumberData,
          formattedPhoneNumber,
          phoneNumber,
          type,
          usageType,
        };
      });
    },
  });
};

const useGetAvailableFOCDates = (
  request: GetAvailableFOCDatesRequest,
  options?: UseQueryOptions<
    GetAvailableFOCDatesResponse,
    AxiosError<any>,
    GetAvailableFOCDatesResponse['focDates']
  >
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getAvailableFOCDates(request),
    queryFn: () => portingSchemaApi.fetchAvailableFOCDates(request),
    retry: false,
    ...options,
    enabled: !!request.portingDataId && !!request.provider && (options?.enabled ?? true),
    select: (data) => data?.focDates ?? [],
  });
};

const useGetPortingValidationData = (
  request: GetPortingValidationsByPortingDataIDRequest,
  options?: UseQueryOptions<
    GetPortingValidationsByPortingDataIDResponse,
    AxiosError<any>,
    GetPortingValidationsByPortingDataIDResponse['portingValidations']
  >
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPortingValidationData(request),
    queryFn: () => portingSchemaApi.fetchPortingValidationData(request),
    retry: false,
    ...options,
    enabled: !!request.portingDataId && (options?.enabled ?? true),
    select: (data) => data?.portingValidations ?? [],
  });
};

const useCreateSFPortOrder = (
  options?: UseMutationOptions<
    CreateSalesforcePortOrderResponse,
    AxiosError<any>,
    CreateSalesforcePortOrderRequest
  >
) => {
  return useMutation({
    mutationFn: portingSchemaApi.createSFPortOrder,
    ...options,
  });
};

const useCancelPortOrder = (
  options?: UseMutationOptions<unknown, AxiosError<any>, CancelPortInOrderRequest>
) => {
  return useMutation({
    mutationFn: portingSchemaApi.cancelPortInOrder,
    ...options,
  });
};

const useCancelSMSPortOrder = (
  options?: UseMutationOptions<unknown, AxiosError<any>, CancelPortInOrderRequest>
) => {
  return useMutation({
    mutationFn: portingSchemaApi.cancelSMSPortInOrder,
    ...options,
  });
};

const useClonePortOrder = (
  options?: UseMutationOptions<
    ClonePortingDataResponse,
    AxiosError<any>,
    ClonePortingDataRequest
  >
) => {
  return useMutation({
    mutationFn: portingSchemaApi.clonePortInData,
    ...options,
  });
};

type UploadPortDocumentRequest = {
  file: File;
  type: PortingMediaType | OldMediaType;
  portId: string;
};

const useUploadPortDocument = (
  options?: UseMutationOptions<
    UploadPortOrderDocumentResponse,
    AxiosError<any>,
    UploadPortDocumentRequest
  >
) => {
  return useMutation({
    mutationFn: async (request: UploadPortDocumentRequest) => {
      const formData = new FormData();
      formData.append('file', request.file);
      formData.append('type', request.type);
      const mediaData = await uploadPhoneFile(formData);
      return portingSchemaApi.uploadPortOrderDocument({
        contentType: request.file.type,
        mediaData: {
          portingDataId: request.portId,
          mediaId: mediaData.ID,
          fileName: mediaData.Name,
          mediaType: request.type,
        },
      });
    },
    ...options,
  });
};

const useUpdatePortDocument = (
  options?: UseMutationOptions<
    UpdatePortOrderDocumentResponse,
    AxiosError<any>,
    UploadPortDocumentRequest
  >
) => {
  return useMutation({
    mutationFn: async (request: UploadPortDocumentRequest) => {
      const formData = new FormData();
      formData.append('file', request.file);
      formData.append('type', request.type);
      const mediaData = await uploadPhoneFile(formData);
      return portingSchemaApi.updatePortOrderDocument({
        contentType: request.file.type,
        mediaData: {
          portingDataId: request.portId,
          mediaId: mediaData.ID,
          fileName: mediaData.Name,
          mediaType: request.type,
        },
      });
    },
    ...options,
  });
};

const useDeletePortDocument = (
  options?: UseMutationOptions<
    DeletePortOrderDocumentResponse,
    AxiosError<any>,
    DeletePortOrderDocumentRequest
  >
) => {
  return useMutation({
    mutationFn: portingSchemaApi.deletePortOrderDocument,
    ...options,
  });
};

const useGetPortingAuditHistorySummary = <TData = GetPortingAuditSummaryResponse>(
  request: GetPortingAuditSummaryRequest,
  options?: UseQueryOptions<GetPortingAuditSummaryResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPortingAuditHistorySummary(request),
    queryFn: () => portingSchemaApi.fetchPortingAuditHistorySummary(request),
    retry: false,
    ...options,
  });
};

const useGetPortingDetails = <TData = ReadPortingDataByIDResponse>(
  request: ReadPortingDataByIDRequest,
  options?: UseQueryOptions<ReadPortingDataByIDResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPortingDetails(request),
    queryFn: () => portingSchemaApi.getPortingDataById(request),
    retry: false,
    ...options,
  });
};

const useUpdatePortingData = (
  options?: UseMutationOptions<
    PortingDataUpdateResponse,
    AxiosError<any>,
    PortingDataUpdateRequest
  >
) => {
  return useMutation({
    mutationFn: portingSchemaApi.updatePortingDataById,
    ...options,
  });
};

const useGetPortOrderList = <TData = PortingDataByLocationIDResponse>(
  request: { locationId: string },
  options?: UseQueryOptions<PortingDataByLocationIDResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPortOrderList(request.locationId),
    queryFn: () => portingSchemaApi.getPortingDataByLocationIds([request.locationId]),
    retry: false,
    enabled: !!request.locationId && (options?.enabled ?? true),
    ...options,
  });
};

const useGetPortingAuditRevisionDetails = <
  TData = GetPortingAuditRevisionDetailsResponse
>(
  request: GetPortingAuditRevisionDetailsRequest,
  options?: UseQueryOptions<
    GetPortingAuditRevisionDetailsResponse,
    AxiosError<any>,
    TData
  >
) => {
  return useQuery({
    queryKey: PortingQueryKeys.getPortingAuditRevisionDetails(request),
    queryFn: () => portingSchemaApi.fetchPortingAuditRevisionDetails(request),
    retry: false,
    ...options,
  });
};

const getCheckPhoneNumbersExistQueryOptions = (numbers: string[]) => ({
  queryKey: [...PortingQueryKeys.baseQueryKey, 'check-phone-numbers-exist', ...numbers],
  queryFn: () =>
    PhoneNumberService.CheckPhoneNumbersExist(authorizedFetch(), { numbers }),
  cacheTime: 5 * 60 * 1000,
  staleTime: 5 * 60 * 1000,
});

const useCheckPhoneNumbersExists = <TData = CheckPhoneNumbersExistResponse>(
  request: CheckPhoneNumbersExistRequest,
  options: UseQueryOptions<CheckPhoneNumbersExistResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    ...getCheckPhoneNumbersExistQueryOptions(request.numbers ?? []),
    ...options,
    enabled: !!request.numbers?.length && (options?.enabled ?? true),
  });
};

const useCheckSmsHostabilityBatch = (phoneNumbers: string[], enabled = true) => {
  return useQueries(
    phoneNumbers.map((phoneNumber) => ({
      queryKey: PortingQueryKeys.checkSmsHostability(phoneNumber),
      queryFn: () =>
        PortingDataService.SMSHostabilityCheck(authorizedFetch(), { phoneNumber }),
      retry: false,
      enabled: !!phoneNumber && enabled,
    }))
  ) as UseQueryResult<SMSHostabilityCheckResponse, AxiosError<Error>>[];
};

const useSubmitSmsHostingOrder = (
  options?: UseMutationOptions<
    PortInSMSCreateByPortingDataIDResponse,
    AxiosError<Error>,
    PortInSMSCreateByPortingDataIDRequest
  >
) => {
  return useMutation({
    mutationFn: (request: PortInSMSCreateByPortingDataIDRequest) =>
      PortingDataService.PortInSMSCreateByPortingDataID(authorizedFetch(), request),
    ...options,
  });
};

type CheckSmsHostabilityBatchRequest = {
  phoneNumbers: string[];
};

type CheckSmsHostabilityBatchResponse = {
  results: SMSHostabilityCheckResponse[];
};

const useCheckSmsHostabilityBatchMutation = (
  options?: UseMutationOptions<
    CheckSmsHostabilityBatchResponse,
    AxiosError<Error>,
    CheckSmsHostabilityBatchRequest
  >
) => {
  return useMutation({
    mutationFn: async (request: CheckSmsHostabilityBatchRequest) => {
      const results = await Promise.all(
        request.phoneNumbers.map((phoneNumber) =>
          PortingDataService.SMSHostabilityCheck(authorizedFetch(), { phoneNumber })
        )
      );
      return { results };
    },
    ...options,
  });
};

export const PortingQueries = {
  PortingQueryKeys,
  useCancelPortOrder,
  useCancelSMSPortOrder,
  useClonePortOrder,
  useCreateSFPortOrder,
  useGetAvailableFOCDates,
  useGetPhoneNumbers,
  useGetPortingValidationData,
  useUploadPortDocument,
  useDeletePortDocument,
  useUpdatePortDocument,
  useGetPortingAuditHistorySummary,
  useGetPortingAuditRevisionDetails,
  useGetPortingDetails,
  useUpdatePortingData,
  useGetPortOrderList,
  useCheckPhoneNumbersExists,
  getCheckPhoneNumbersExistQueryOptions,
  useCheckSmsHostabilityBatch,
  useCheckSmsHostabilityBatchMutation,
  useSubmitSmsHostingOrder,
};
